/**
 * Dropdown — 트리거 기반 드롭다운 메뉴 어댑터 (shadcn DropdownMenu 위임)
 *
 * 기존 public API(DropdownActionItem, DropdownSeparator, DropdownItem, DropdownProps)를 완전 보존.
 * - trigger prop → DropdownMenuTrigger 래핑
 * - items 배열 → DropdownMenuItem / DropdownMenuSeparator 렌더
 * - danger → variant="destructive"
 * - disabled → Radix Item disabled prop
 * - align → DropdownMenuContent align prop
 * - className → DropdownMenuTrigger에 전달
 */

import { ReactNode } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export interface DropdownActionItem {
  label: string
  icon?: ReactNode
  onClick?: () => void
  danger?: boolean
  disabled?: boolean
}

export interface DropdownSeparator {
  type: 'separator'
}

export type DropdownItem = DropdownActionItem | DropdownSeparator

interface DropdownProps {
  trigger: ReactNode
  items: DropdownItem[]
  align?: 'left' | 'right'
  className?: string
}

export function Dropdown({ trigger, items, align = 'left', className }: DropdownProps) {
  // DropdownMenuContent의 align은 'start'|'end'|'center'. 기존 'left'→'start', 'right'→'end'로 매핑
  const contentAlign = align === 'right' ? 'end' : 'start'

  return (
    <DropdownMenu>
      {/* asChild로 trigger 노드를 그대로 렌더, className은 컨테이너 래퍼 대신 trigger에 전달 */}
      <DropdownMenuTrigger asChild className={className}>
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={contentAlign}>
        {items.map((item, index) => {
          // separator 구분
          if ('type' in item && item.type === 'separator') {
            return <DropdownMenuSeparator key={index} />
          }
          const action = item as DropdownActionItem
          return (
            <DropdownMenuItem
              key={index}
              variant={action.danger ? 'destructive' : 'default'}
              disabled={action.disabled}
              onClick={() => {
                if (!action.disabled) action.onClick?.()
              }}
            >
              {action.icon && <span>{action.icon}</span>}
              {action.label}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
